import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, DetailField } from './AcademyShared';

export default function AcademyCertificates() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [tab, setTab] = useState('certificates');
  const [certs, setCerts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [issuing, setIssuing] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([
        fetch(`${API}/companies/${companyId}/academy/certificates?per_page=50`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API}/companies/${companyId}/academy/certificate-templates?per_page=50`, { headers: getAuthHeaders() }).then(r => r.json()),
      ]);
      setCerts(Array.isArray(c.data) ? c.data : []);
      setTemplates(Array.isArray(t.data) ? t.data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/enrollments?per_page=500&status=completed`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setEnrollments(Array.isArray(j.data) ? j.data : [])).catch(() => setEnrollments([]));
  }, [companyId]);

  const revoke = async (id) => {
    const reason = window.prompt('Revocation reason:');
    if (reason === null) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/certificates/${id}/revoke`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Revoke failed'); return; }
    fetchAll();
  };

  const changeTemplateStatus = async (id, status) => {
    await fetch(`${API}/companies/${companyId}/academy/certificate-templates/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchAll();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏆 Certificates</h2>
        {tab === 'certificates' ? <button style={btnPrimary} onClick={() => setIssuing(true)}>+ Issue Certificate</button>
          : <button style={btnPrimary} onClick={() => setUploadingTemplate(true)}>+ Upload Template</button>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={tab === 'certificates' ? btnPrimary : btnSecondary} onClick={() => setTab('certificates')}>Certificates</button>
        <button style={tab === 'templates' ? btnPrimary : btnSecondary} onClick={() => setTab('templates')}>Templates</button>
      </div>

      {tab === 'certificates' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Cert #', 'Student', 'Type', 'Issue Date', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
                {!loading && certs.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No certificates</td></tr>}
                {certs.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{c.certificate_number}</td>
                    <td style={tdStyle}>{c.enrollment?.student ? c.enrollment.student.full_name : '—'}</td>
                    <td style={tdStyle}>{c.certificate_type}</td>
                    <td style={tdStyle}>{c.issue_date}</td>
                    <td style={tdStyle}><Badge value={c.status} /></td>
                    <td style={tdStyle}>
                      <a style={{ ...btnSmall, textDecoration: 'none', display: 'inline-block', marginRight: 6 }} href={`/verify-certificate/${c.qr_token}`} target="_blank" rel="noreferrer">Verify Link</a>
                      {c.status !== 'revoked' && <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => revoke(c.id)}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'templates' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Name', 'Analysis', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {initialLoading && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
                {!loading && templates.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No templates</td></tr>}
                {templates.map(t => (
                  <tr key={t.id}>
                    <td style={{ ...tdStyle, fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => setViewingTemplate(t)}>{t.name}</td>
                    <td style={tdStyle}><Badge value={t.analysis_status} /></td>
                    <td style={tdStyle}><Badge value={t.status} /></td>
                    <td style={tdStyle}>
                      <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewingTemplate(t)}>Fields</button>
                      {t.status === 'active' ? <button style={btnSmall} onClick={() => changeTemplateStatus(t.id, 'archived')}>Archive</button>
                        : <button style={btnSmall} onClick={() => changeTemplateStatus(t.id, 'active')}>Activate</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {issuing && <IssueCertModal companyId={companyId} enrollments={enrollments} templates={templates} onClose={() => setIssuing(false)} onSaved={() => { setIssuing(false); fetchAll(); }} />}
      {uploadingTemplate && <UploadTemplateModal companyId={companyId} onClose={() => setUploadingTemplate(false)} onSaved={() => { setUploadingTemplate(false); fetchAll(); }} />}
      {viewingTemplate && <TemplateDetail companyId={companyId} templateId={viewingTemplate.id} onClose={() => { setViewingTemplate(null); fetchAll(); }} />}
    </div>
  );
}

function IssueCertModal({ companyId, enrollments, templates, onClose, onSaved }) {
  const [form, setForm] = useState({ enrollment_id: '', template_id: '', certificate_type: 'completion', issue_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.enrollment_id) { alert('Enrollment is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/certificates`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Issue Certificate</h3>
        <FormRow label="Enrollment (completed) *">
          <select style={inputStyle} value={form.enrollment_id} onChange={e => setForm({ ...form, enrollment_id: e.target.value })}>
            <option value="">Select enrollment</option>
            {enrollments.map(en => <option key={en.id} value={en.id}>{en.student ? en.student.full_name : en.enrollment_code} — {en.cohort?.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Template">
          <select style={inputStyle} value={form.template_id} onChange={e => setForm({ ...form, template_id: e.target.value })}>
            <option value="">Default</option>
            {templates.filter(t => t.status === 'active').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Type">
            <select style={inputStyle} value={form.certificate_type} onChange={e => setForm({ ...form, certificate_type: e.target.value })}>
              {['completion', 'achievement', 'participation'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </FormRow>
          <FormRow label="Issue Date"><input type="date" style={inputStyle} value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} /></FormRow>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Issue'}</button>
        </div>
      </div>
    </div>
  );
}

function UploadTemplateModal({ companyId, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !file) { alert('Name and file are required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('file', file);
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API}/companies/${companyId}/academy/certificate-templates`, {
        method: 'POST', headers, body: fd,
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Upload failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Upload Certificate Template</h3>
        <FormRow label="Name *"><input style={inputStyle} value={name} onChange={e => setName(e.target.value)} /></FormRow>
        <FormRow label="Template File *"><input type="file" onChange={e => setFile(e.target.files[0])} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Uploading...' : 'Upload'}</button>
        </div>
      </div>
    </div>
  );
}

function TemplateDetail({ companyId, templateId, onClose }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [analyzing, setAnalyzing] = useState(false);
  const [addingField, setAddingField] = useState(false);
  const [fieldForm, setFieldForm] = useState({ field_key: '', label: '', x: '', y: '', font: '', font_size: '' });

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/certificate-templates/${templateId}`, { headers: getAuthHeaders() });
      setTemplate(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, templateId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/certificate-templates/${templateId}/analyze`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Analysis failed — you can map fields manually below.'); }
      fetchDetail();
    } catch (e) { console.error(e); }
    setAnalyzing(false);
  };

  const addField = async () => {
    if (!fieldForm.field_key.trim()) { alert('Field key required'); return; }
    const res = await fetch(`${API}/companies/${companyId}/academy/certificate-templates/${templateId}/fields`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(fieldForm),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to add field'); return; }
    setAddingField(false);
    setFieldForm({ field_key: '', label: '', x: '', y: '', font: '', font_size: '' });
    fetchDetail();
  };

  const removeField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/certificate-templates/${templateId}/fields/${fieldId}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchDetail();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 620 }} onClick={e => e.stopPropagation()}>
        {initialLoading || !template ? <div>Loading...</div> : (
          <>
            <h3 style={{ marginTop: 0 }}>{template.name} <Badge value={template.analysis_status} /></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <DetailField label="Status" value={<Badge value={template.status} />} />
              <DetailField label="Analysis Status" value={<Badge value={template.analysis_status} />} />
            </div>
            <button style={{ ...btnSecondary, marginBottom: 16 }} onClick={analyze} disabled={analyzing}>{analyzing ? 'Analyzing...' : '🔍 Run AI Field Analysis'}</button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>Field Mapping</h4>
              <button style={btnSmall} onClick={() => setAddingField(v => !v)}>{addingField ? 'Cancel' : '+ Add Field'}</button>
            </div>
            {addingField && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="Field Key *"><input style={inputStyle} value={fieldForm.field_key} onChange={e => setFieldForm({ ...fieldForm, field_key: e.target.value })} placeholder="e.g. student_name" /></FormRow>
                  <FormRow label="Label"><input style={inputStyle} value={fieldForm.label} onChange={e => setFieldForm({ ...fieldForm, label: e.target.value })} /></FormRow>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="X"><input type="number" style={inputStyle} value={fieldForm.x} onChange={e => setFieldForm({ ...fieldForm, x: e.target.value })} /></FormRow>
                  <FormRow label="Y"><input type="number" style={inputStyle} value={fieldForm.y} onChange={e => setFieldForm({ ...fieldForm, y: e.target.value })} /></FormRow>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="Font"><input style={inputStyle} value={fieldForm.font} onChange={e => setFieldForm({ ...fieldForm, font: e.target.value })} /></FormRow>
                  <FormRow label="Font Size"><input type="number" style={inputStyle} value={fieldForm.font_size} onChange={e => setFieldForm({ ...fieldForm, font_size: e.target.value })} /></FormRow>
                </div>
                <button style={btnPrimary} onClick={addField}>Save Field</button>
              </div>
            )}
            <table style={tableStyle}>
              <thead><tr>{['Field Key', 'Label', 'Position', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(template.fields || []).length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No fields mapped</td></tr>}
                {(template.fields || []).map(f => (
                  <tr key={f.id}>
                    <td style={tdStyle}>{f.field_key}</td>
                    <td style={tdStyle}>{f.label}</td>
                    <td style={tdStyle}>{f.x},{f.y}</td>
                    <td style={tdStyle}><button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeField(f.id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnSecondary} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
